import React, { useState } from 'react';
import { Box } from '@mui/system';
import {
  Button,
  Container,
  Alert,
  Snackbar,
  List,
  ListSubheader,
  TextField,
  CircularProgress,
  IconButton,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { usePolls, usePollVotes } from '../../services';
import { User } from '../../types';

const PollVoteSummary = ({ pollId }: { pollId: string }) => {
  const { votes } = usePollVotes(pollId);
  return (
    <Chip
      label={`${votes.length} vote${votes.length !== 1 ? 's' : ''}`}
      size="small"
      sx={{ ml: 1 }}
    />
  );
};

const Polls = ({ user }: { user: User }) => {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [multipleChoice, setMultipleChoice] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [copySnackOpen, setCopySnackOpen] = useState(false);
  const { polls, createPoll, deletePoll, togglePollActive } = usePolls();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pollToDelete, setPollToDelete] = useState<string | null>(null);

  const handleAddOption = () => {
    setOptions([...options, '']);
  };

  const handleOptionChange = (index: number, value: string) => {
    const updated = [...options];
    updated[index] = value;
    setOptions(updated);
  };

  const handleRemoveOption = (index: number) => {
    if (options.length <= 2) return;
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    const validOptions = options.filter((o) => o.trim());
    if (!question.trim() || validOptions.length < 2) return;
    setSaving(true);
    await createPoll({
      question: question.trim(),
      options: validOptions,
      createdOn: new Date(),
      creatorId: user.userId,
      active: true,
      multipleChoice,
    });
    setSaving(false);
    setSnackOpen(true);
    setQuestion('');
    setOptions(['', '']);
    setMultipleChoice(false);
  };

  const handleDeleteClick = (id: string) => {
    setPollToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (pollToDelete) {
      await deletePoll(pollToDelete);
      setDeleteDialogOpen(false);
      setPollToDelete(null);
    }
  };

  const copyLink = (pollId: string) => {
    const url = `${window.location.origin}/poll/${pollId}`;
    navigator.clipboard.writeText(url);
    setCopySnackOpen(true);
  };

  const canSubmit =
    question.trim() && options.filter((o) => o.trim()).length >= 2;

  if (!user?.isAdmin) return null;

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        <h2>Create a new Poll</h2>
        <TextField
          label="Question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          style={{ marginBottom: '1rem', minWidth: '400px' }}
          multiline
        />
        {options.map((option, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              gap: 1,
            }}
          >
            <TextField
              label={`Option ${index + 1}`}
              value={option}
              onChange={(e) => handleOptionChange(index, e.target.value)}
              style={{ minWidth: '350px' }}
            />
            {options.length > 2 && (
              <IconButton
                size="small"
                onClick={() => handleRemoveOption(index)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))}
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={handleAddOption}
          sx={{ mb: 2 }}
        >
          Add Option
        </Button>
        <FormControlLabel
          control={
            <Switch
              checked={multipleChoice}
              onChange={(e) => setMultipleChoice(e.target.checked)}
            />
          }
          label="Allow multiple selections"
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : null}
          disabled={saving || !canSubmit}
          onClick={handleSubmit}
        >
          {saving ? 'Creating...' : 'Create Poll'}
        </Button>

        <List
          subheader={<ListSubheader sx={{ mt: 5 }}>Polls</ListSubheader>}
          sx={{ width: '100%' }}
        >
          {polls.map((poll) => (
            <React.Fragment key={poll.id}>
              <ListItem>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {poll.question}
                      <Chip
                        label={poll.active ? 'Active' : 'Closed'}
                        color={poll.active ? 'success' : 'default'}
                        size="small"
                      />
                      <PollVoteSummary pollId={poll.id} />
                    </Box>
                  }
                  secondary={`${poll.options.length} options - ${poll.multipleChoice ? 'Multiple choice' : 'Single choice'} - Created ${poll.createdOn.toLocaleDateString()}`}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Copy shareable link">
                    <IconButton
                      edge="end"
                      onClick={() => copyLink(poll.id)}
                      sx={{ mr: 1 }}
                    >
                      <ContentCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => togglePollActive(poll.id, !poll.active)}
                    sx={{ mr: 1 }}
                  >
                    {poll.active ? 'Close' : 'Reopen'}
                  </Button>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleDeleteClick(poll.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this poll? All votes will be lost.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="primary" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackOpen}
          autoHideDuration={6000}
          onClose={() => setSnackOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackOpen(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            Poll Created Successfully
          </Alert>
        </Snackbar>
        <Snackbar
          open={copySnackOpen}
          autoHideDuration={3000}
          onClose={() => setCopySnackOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setCopySnackOpen(false)}
            severity="info"
            sx={{ width: '100%' }}
          >
            Poll link copied to clipboard
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default Polls;
